/**
 * "Needs attention" cases someone closed by hand (Overview).
 *
 *   attention_resolved/<key>   one doc per closed case, shared by every user:
 *                              { kind, label, reason, resolved_by, resolved_at, last_updated }
 *
 * A case is closed even when it was not fixed (a stolen car, a car under a legal case cannot be
 * renewed). The key carries what makes the case unique (a car's plate AND its expiry date), so
 * a new problem on the same car - a later expiry - shows again.
 */
import { collection, deleteDoc, doc, documentId, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';

const COL = 'attention_resolved';
/** Ids per `documentId() in` query (the Firestore limit is 30). */
const IN_CHUNK = 30;
/** Keys per by-keys call on the store (the engine's limit is 1000). */
const STORE_CHUNK = 1000;
// The new store (USE_CONTRACTS_V2, owner 27 Sep 2026: "we will not rely on Firebase anymore"): the same cases in the
// engine's PostgreSQL (POST /api/store/attention/resolved/by-keys, PUT / DELETE /api/store/attention/resolved/{key});
// a reopened case is kept there as reopened, not deleted. A refused call throws an Error with `code`
// ('permission-denied' for a 403), as Firestore's did.
const viaStore = (call) => call().catch((e) => { throw officeError(e); });

/** The case keys not asked for yet, unique, in chunks of 30 (one query each). */
export const keyChunks = (keys, known = new Set()) => {
    const wanted = Array.from(new Set((keys || []).filter((k) => k && !known.has(k))));
    const chunks = [];
    for (let i = 0; i < wanted.length; i += IN_CHUNK) chunks.push(wanted.slice(i, i + IN_CHUNK));
    return chunks;
};

/** Firestore-safe document id for one case. */
export const attentionKey = (kind, ...parts) =>
    [kind, ...parts].map((p) => String(p ?? '').trim().replace(/[/\s.#$[\]]+/g, '-')).join('__').slice(0, 300);

export const RESOLVE_REASONS = ['Renewed', 'Stolen', 'Legal case', 'Sold / off fleet', 'Other'];

export const attentionService = {
    /**
     * key -> the stored record, for the given case keys only. The Overview asks for
     * the cases it is showing: the collection keeps every case ever closed (a new
     * Ejar error or engine check is a new key), so reading it whole on every open
     * grew without end (audit ADM-23). `known` = keys already asked for.
     */
    async resolvedFor(keys, known) {
        const out = {};
        if (USE_CONTRACTS_V2) {
            const wanted = keyChunks(keys, known).flat();
            for (let i = 0; i < wanted.length; i += STORE_CHUNK) {
                Object.assign(out, await viaStore(() => finesService.storeAttentionResolved(wanted.slice(i, i + STORE_CHUNK))));
            }
            return out;
        }
        for (const chunk of keyChunks(keys, known)) {
            const snap = await getDocs(query(collection(db, COL), where(documentId(), 'in', chunk)));
            snap.docs.forEach((d) => { out[d.id] = { id: d.id, ...d.data() }; });
        }
        return out;
    },

    async resolve(key, { kind, label, reason, by }) {
        if (USE_CONTRACTS_V2) {
            await viaStore(() => finesService.storeAttentionResolve(key, { kind, label, reason: reason || 'Other', by: by || '' }));
            return;
        }
        await setDoc(doc(db, COL, key), {
            kind, label, reason: reason || 'Other', resolved_by: by || '',
            resolved_at: serverTimestamp(), last_updated: serverTimestamp(),
        });
    },

    /** undo: the case shows again */
    async reopen(key) {
        if (USE_CONTRACTS_V2) {
            await viaStore(() => finesService.storeAttentionReopen(key));
            return;
        }
        await deleteDoc(doc(db, COL, key));
    },
};

export default attentionService;
